import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { getAllServices } from '../services/servicesStore';
import { currencyService } from '../services/currencyService';
import {
  getServiceBillingLabel,
  getServiceOptionsSummary,
  getServicePricePrefix,
  getServicePrimaryCtaLabel,
  hasStartingPrice,
} from '../services/serviceCatalogUtils';

const showcaseServiceIds = [
  'web-conception',
  'ecommerce-premium',
  'tunnel-ia',
  'agent-ia-interne',
  'publicites',
  'veille-ia-contenu',
];

const ServiceCard = ({ service }: { service: any }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.12,
  });
  const [, setCountry] = useState(currencyService.getCountry());

  useEffect(() => {
    const unsubscribe = currencyService.subscribe((nextCountry) => {
      setCountry(nextCountry);
    });

    return unsubscribe;
  }, []);

  const klarnaEligible = currencyService.canUseKlarna(
    service.price,
    service.billingType ?? 'one-time'
  );

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55 }}
      className="relative group"
    >
      <div className="absolute inset-0 rounded-[28px] bg-gradient-to-br from-neon-orange/20 via-transparent to-blue-500/10 opacity-0 blur-xl transition-opacity duration-300 group-hover:opacity-100" />

      <div className="relative h-full overflow-hidden rounded-[28px] border border-gray-100 bg-white shadow-lg transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-2xl">
        {service.image && (
          <div className="aspect-[1.45] overflow-hidden bg-[#0f1728]">
            <img
              src={service.image}
              alt={service.name}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            />
          </div>
        )}

        <div className="p-7">
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <div className="mb-3 inline-flex rounded-full bg-neon-orange/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-neon-orange">
                {service.category}
              </div>
              <h3 className="text-2xl font-bold leading-tight text-gray-900">{service.name}</h3>
            </div>
            {service.popular && (
              <span className="rounded-full bg-gray-900 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-white">
                Premium
              </span>
            )}
          </div>

          <p className="mb-6 text-gray-600">{service.description}</p>

          <div className="mb-7 flex flex-wrap gap-2">
            {service.features.slice(0, 3).map((feature: string, index: number) => (
              <span
                key={index}
                className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-sm text-gray-600"
              >
                {feature}
              </span>
            ))}
          </div>

          <div className="mb-6 flex items-end justify-between gap-4 border-t border-gray-100 pt-5">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-400">
                {getServicePricePrefix(service)}
              </div>
              <div className="mt-2 text-3xl font-bold text-neon-orange">
                {currencyService.formatPrice(service.price)}
              </div>
              <div className="mt-2 text-sm text-gray-500">
                {getServiceBillingLabel(service)}
              </div>
            </div>

            <div className="max-w-[180px] text-right text-sm text-gray-500">
              {klarnaEligible && !hasStartingPrice(service)
                ? `3x Klarna possible: ${currencyService.formatPrice(
                    currencyService.getKlarnaInstallmentAmount(service.price)
                  )}`
                : getServiceOptionsSummary(service) || 'Base agence'}
            </div>
          </div>

          <Link
            to={`/acheter/${service.id}`}
            className="block w-full rounded-full bg-gradient-to-r from-neon-orange to-[#FF4500] px-6 py-3 text-center font-semibold text-white"
          >
            {getServicePrimaryCtaLabel(service)}
          </Link>
        </div>
      </div>
    </motion.div>
  );
};

const Services = () => {
  const [services, setServices] = useState<any[]>([]);
  const [ref] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  useEffect(() => {
    getAllServices()
      .then((data) => setServices(data))
      .catch((error) => console.error('Erreur lors du chargement des services :', error));
  }, []);

  const showcaseServices = showcaseServiceIds
    .map((serviceId) => services.find((service) => service.id === serviceId))
    .filter((service): service is NonNullable<typeof service> => Boolean(service));

  return (
    <section id="services" className="bg-gradient-to-b from-gray-50 to-white py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-16 text-center"
        >
          <div className="mb-5 inline-flex rounded-full border border-neon-orange/20 bg-neon-orange/10 px-5 py-2 text-sm font-semibold uppercase tracking-[0.22em] text-neon-orange">
            Catalogue agence
          </div>
          <h2 className="section-title">Des bases claires, des options utiles, une vraie couverture agence</h2>
          <p className="section-subtitle">
            Sites, media buying, CRM, IA, e-commerce, design, analytics, funnels et operations:
            on couvre les briques qui aident une entreprise a vendre, livrer et grandir proprement.
          </p>
        </motion.div>

        <div ref={ref} className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {showcaseServices.map((service) => (
            <ServiceCard key={service.id} service={service} />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-12 text-center"
        >
          <Link
            to="/services"
            className="inline-block rounded-full bg-gray-900 px-8 py-4 text-lg font-semibold text-white"
          >
            Explorer tous les services
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
